import { truncate } from 'lodash';

function truncateString(str: string, length: number, omission: string = '...'): string {
  if (str.length <= length) return str;
  return str.slice(0, length - omission.length) + omission;
}

interface FormattedLog {
  timestamp: string;
  level: string;
  message: string;
  details: Record<string, any>;
}

const MAX_STRING_LENGTH = 300;
const TRUNCATE_SUFFIX = ' [truncated...]';
const TRUNCATE_OBJECT_MESSAGE = '\n[Content truncated. Use isFullLog: true to see complete data]';

export class LogFormatter {
  private static sensitiveKeys = ['pid', 'hostname', '_formatted'];
  private static systemKeys = ['time', 'level', 'msg', 'pid', 'hostname'];
  private static maxObjectKeys = 10;

  private static truncateValue(value: any, isFullLog = false, depth = 0): any {
    if (isFullLog) return value;
    if (depth > 2) return '[Nested Object]';

    if (typeof value === 'string') {
      if (value.length <= MAX_STRING_LENGTH) return value;
      return truncateString(value, MAX_STRING_LENGTH, TRUNCATE_SUFFIX);
    }

    if (Array.isArray(value)) {
      if (value.length > this.maxObjectKeys) {
        return [
          ...value.slice(0, this.maxObjectKeys).map(item => this.truncateValue(item, isFullLog, depth + 1)),
          `[${value.length - this.maxObjectKeys} more items...]`
        ];
      }
      return value.map(item => this.truncateValue(item, isFullLog, depth + 1));
    }

    if (typeof value === 'object' && value !== null) {
      const entries = Object.entries(value);
      if (entries.length > this.maxObjectKeys) {
        const truncatedObj: Record<string, any> = {};
        entries.slice(0, this.maxObjectKeys).forEach(([key, val]) => {
          truncatedObj[key] = this.truncateValue(val, isFullLog, depth + 1);
        });
        truncatedObj['_truncated'] = `[${entries.length - this.maxObjectKeys} more fields...]`;
        return truncatedObj;
      }

      const truncatedObj: Record<string, any> = {};
      for (const [key, val] of entries) {
        truncatedObj[key] = this.truncateValue(val, isFullLog, depth + 1);
      }
      return truncatedObj;
    }

    return value;
  }

  private static cleanObject(obj: Record<string, any>, isFullLog = false): Record<string, any> {
    const cleaned: Record<string, any> = {};
    let isLargeObject = false;

    for (const [key, value] of Object.entries(obj)) {
      if (!isFullLog && this.sensitiveKeys.includes(key)) continue;
      if (!this.systemKeys.includes(key)) {
        cleaned[key] = this.truncateValue(value, isFullLog);
        if (typeof value === 'object' && value !== null && Object.keys(value).length > this.maxObjectKeys) {
          isLargeObject = true;
        }
      }
    }

    if (isLargeObject && !isFullLog) {
      cleaned['_note'] = TRUNCATE_OBJECT_MESSAGE;
    }

    return cleaned;
  }

  static format(logEntry: string | Record<string, any>, isFullLog = false): FormattedLog {
    const log = typeof logEntry === 'string' ? JSON.parse(logEntry) : logEntry;

    const formatted: FormattedLog = {
      timestamp: new Date(log.time).toLocaleTimeString(),
      level: log.level || 'INFO',
      message: log.msg || '',
      details: this.cleanObject(log, isFullLog)
    };

    return formatted;
  }

  static formatToString(logEntry: string | Record<string, any>, isFullLog = false): string {
    const formatted = this.format(logEntry, isFullLog);
    const details = Object.keys(formatted.details).length > 0 
      ? `\n${JSON.stringify(formatted.details, null, 2)}`
      : '';

    return `[${formatted.timestamp}] ${formatted.level}: ${formatted.message}${details}`;
  }

  static formatBatch(logs: (string | Record<string, any>)[], isFullLog = false): string {
    return logs
      .map(log => this.formatToString(log, isFullLog))
      .join('\n');
  }
} 